"use client";

import { useState, useEffect, Suspense } from "react";
import { Search, SearchX, ChevronRight, Stethoscope, BriefcaseMedical, Syringe, HeartPulse } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useI18n } from "@/i18n/I18nProvider";
import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import DemoNote from "@/components/ui/DemoNote";
import { getServices } from "@/services/mockService";
import type { MedicalService } from "@/data/mockData";

const categories = ["All", "Health Checkup", "Medical Treatment", "Surgery Services", "Recovery & Rehabilitation"];
const categoryMap: Record<string, string> = {
  "health-checkup": "Health Checkup", "medical-treatment": "Medical Treatment",
  "surgery": "Surgery Services", "recovery": "Recovery & Rehabilitation",
};
const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  stethoscope: Stethoscope, hospital: BriefcaseMedical, scalpel: Syringe, "heart-pulse": HeartPulse,
};

function ServicesContent() {
  const { t } = useI18n();
  const searchParams = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(() => {
    const cat = searchParams.get("category");
    return (cat && categoryMap[cat]) || "All";
  });
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [services, setServices] = useState<MedicalService[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getServices(activeCategory === "All" ? undefined : activeCategory).then((data) => {
      if (!alive) return;
      setServices(data);
      setLoadedFor(activeCategory);
    });
    return () => { alive = false; };
  }, [activeCategory]);

  const loading = loadedFor !== activeCategory;

  const filtered = search
    ? services.filter((s) => s.title.toLowerCase().includes(search.toLowerCase()) || s.description.toLowerCase().includes(search.toLowerCase()))
    : services;

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Medical Services")}
        subtitle={t("Specialist care across checkup, treatment, surgery and recovery")}
      >
        <div className="relative mt-3.5">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-dark/35" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Search services...")}
            aria-label={t("Search services...")}
            className="cc-input pl-11"
          />
        </div>
      </PageHeader>

      <DemoNote text={t("Demo price notice")} />

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-5 py-3 bg-white border-b border-[#EDF1F1]">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`cc-chip ${activeCategory === cat ? "cc-chip-active" : ""}`}
          >
            {t(cat)}
          </button>
        ))}
      </div>

      {/* Service list */}
      <div className="px-5 py-4 space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="cc-skeleton h-[104px]" />)
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title={t("No services found")}
            hint={t("Try a different keyword or choose another care category.")}
          />
        ) : (
          filtered.map((service) => (
            <Link key={service.id} href={`/services/${service.id}`} className="block">
              <div className="cc-card p-4 active:scale-[0.98] transition-transform">
                <div className="flex items-start gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: service.bgColor }}
                  >
                    {(() => {
                      const Icon = iconMap[service.icon] ?? Stethoscope;
                      return (
                        <span style={{ color: service.color }}>
                          <Icon size={22} />
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] font-bold leading-[1.3] text-text-dark">{service.title}</h3>
                    <p className="mt-1 text-[14px] leading-[1.45] text-text-dark/55 line-clamp-2">{service.description}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-text-dark/45">
                      <span className="whitespace-nowrap">{t("Example Estimate")}: {service.priceRange}</span>
                      <span className="w-px h-3 bg-text-dark/10" />
                      <span className="whitespace-nowrap">{service.duration}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-text-dark/25 shrink-0 mt-1" />
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <Suspense fallback={<div className="pb-24 px-5 pt-4"><div className="cc-skeleton h-[104px]" /></div>}>
      <ServicesContent />
    </Suspense>
  );
}
